import React, { useState, useEffect } from 'react';
import './App.css';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const firebaseConfig = {
  apiKey: "AIzaSyCssPmZEr123f4QumVj8Uw-mZKPm4_L9pc",
  authDomain: "visualizer-f2095.firebaseapp.com",
  projectId: "visualizer-f2095",
  storageBucket: "visualizer-f2095.firebasestorage.app",
  messagingSenderId: "632056453495",
  appId: "1:632056453495:web:c22f700e14a66f5e06a972"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// VIEW TABS CONFIGURATION
const VIEW_TABS_CONFIG = {
  basic: {
    label: 'Basic',
    tabId: 'basic',
    description: 'Company List and Top Applicants analysis',
    defaultEnabled: true
  },
  innovation: {
    label: 'Innovation',
    tabId: 'innovation',
    description: 'Visualize LLM outputs',
    defaultEnabled: true
  },
  trends: {
    label: 'Trends',
    tabId: 'trends',
    description: 'Temporal analysis and trends',
    defaultEnabled: false
  },
  citations: {
    label: 'Advanced',
    tabId: 'citations',
    description: 'Citation-based patent analysis',
    defaultEnabled: false
  }
};

// COLOR PALETTE CONFIGURATION
const COLOR_PALETTES = {
  defaultBlue: {
    name: 'Default Blue',
    monochrome: ['#08306B', '#08519C', '#2171B5', '#4292C6', '#6BAED6'],
    multicolor: ['#2171B5', '#6BAED6', '#9ECAE1', '#C6DBEF', '#4A90E2', '#7B68EE', '#9370DB', '#BA55D3', '#8A2BE2', '#6495ED']
  },
  geVernova: {
    name: 'Verde Green',
    monochrome: ['#005733', '#006B3F', '#00A758', '#33C47B', '#66D99E'],
    multicolor: ['#00A758', '#00B862', '#33C47B', '#66D99E', '#00904A', '#006B3F', '#005733', '#99E6BB', '#B2ECC9', '#CCF2D7']
  },
  purpleTech: {
    name: 'Purple Tech',
    monochrome: ['#4A148C', '#6A1B9A', '#8E24AA', '#AB47BC', '#CE93D8'],
    multicolor: ['#8E24AA', '#AB47BC', '#BA68C8', '#CE93D8', '#7B68EE', '#9370DB', '#8A2BE2', '#9932CC', '#DA70D6', '#DDA0DD']
  },
  emeraldGreen: {
    name: 'Emerald Green',
    monochrome: ['#1B5E20', '#2E7D32', '#388E3C', '#4CAF50', '#66BB6A'],
    multicolor: ['#2E7D32', '#388E3C', '#4CAF50', '#66BB6A', '#81C784', '#00C853', '#00E676', '#69F0AE', '#76FF03', '#B2FF59']
  },
  neutralGray: {
    name: 'Slate & Accent',
    monochrome: ['#1E293B', '#334155', '#475569', '#64748B', '#94A3B8'],
    multicolor: ['#134E4A', '#115E59', '#0F766E', '#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#CCFBF1', '#F0FDFA']
  },
  wabtecRed: {
    name: 'Wabtec Red',
    monochrome: ['#7F0000', '#A00000', '#CC0000', '#E03030', '#F07070'],
    multicolor: ['#CC0000', '#E03030', '#F07070', '#A00000', '#7F0000', '#FF4444', '#FF7777', '#FFAAAA', '#FFD0D0', '#FFF0F0']
  }
};

// CSV PARSING UTILITIES
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

const parseMultilineCSV = (text) => {
  const rows = [];
  const lines = text.split('\n');
  let currentRow = '';
  let inQuotes = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Count quotes in line
    for (let j = 0; j < line.length; j++) {
      if (line[j] === '"') {
        inQuotes = !inQuotes;
      }
    }

    currentRow += line;

    // If we're not inside quotes, this row is complete
    if (!inQuotes) {
      if (currentRow.trim()) {
        rows.push(currentRow);
      }
      currentRow = '';
    } else {
      // Still inside quotes, add newline and continue
      currentRow += '\n';
    }
  }

  return rows;
};

function AdminBackend({ user, onLogout }) {
  // Field definitions for column mapping
  const MAPPING_FIELDS = {
    basic: {
      label: 'Basic Information',
      fields: {
        'Patent No.': { label: 'Patent Number', required: true },
        'Applicant': { label: 'Applicant/Company', required: true },
        'Title': { label: 'Patent Title', required: false },
        'Abstract': { label: 'Abstract', required: false },
        'Claims': { label: 'Claims', required: false }
      }
    },
    dates: {
      label: 'Date Information',
      fields: {
        'Filing date': { label: 'Filing Date', required: false },
        'Priority date': { label: 'Priority Date', required: false },
        'Publication date': { label: 'Publication Date', required: false }
      }
    },
    citations: {
      label: 'Citations & Family',
      fields: {
        'Citations Made': { label: 'Backward Citations', required: false },
        'Cited by': { label: 'Forward Citations', required: false },
        'Family member count': { label: 'Family Member Count', required: false }
      }
    },
    basicClassification: {
      label: 'Basic Classification',
      fields: {
        'L1': { label: 'L1: Domain Match', required: false },
        'L2': { label: 'L2: Claim Genus', required: false }
      }
    },
    problemSolution: {
      label: 'Problem-solution',
      fields: {
        'L3': { label: 'L3: Problem Detail', required: false },
        'L4': { label: 'L4: Problem Consolidated', required: false },
        'L5': { label: 'L5: (Reserved for future use)', required: false },
        'L6': { label: 'L6: Solution', required: false },
        'L7': { label: 'L7: Solution Consolidated', required: false },
        'L8': { label: 'L8: (Reserved for future use)', required: false }
      }
    },
    technicalSummary: {
      label: 'Technical Summary',
      fields: {
        'L9': { label: 'L9: Technology Text', required: false },
        'L10': { label: 'L10: Technology Keywords', required: false }
      }
    },
    innovationSummary: {
      label: 'Innovation Summary',
      fields: {
        'L11': { label: 'L11: Innovation Text', required: false },
        'L12': { label: 'L12: Innovation Keywords', required: false }
      }
    }
  };

  const [studyName, setStudyName] = useState('');
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [allowedUsers, setAllowedUsers] = useState('');
  const [enabledViews, setEnabledViews] = useState(getDefaultEnabledViews());
  const [uploading, setUploading] = useState(false);
  const [existingStudies, setExistingStudies] = useState([]);
  const [storageMethod, setStorageMethod] = useState('firestore');
  const [permissionsModal, setPermissionsModal] = useState(null);
  const [mappingModal, setMappingModal] = useState(null);
  const [modalAllowedUsers, setModalAllowedUsers] = useState('');
  const [modalMapping, setModalMapping] = useState({});
  const [viewsModal, setViewsModal] = useState(null);
  const [searchFieldsModal, setSearchFieldsModal] = useState(null);
  const [modalEnabledViews, setModalEnabledViews] = useState({});
  const [modalSearchableFields, setModalSearchableFields] = useState({});
  const [searchableFields, setSearchableFields] = useState({
    'Title': true,
    'Abstract': true,
    'Claims': true,
    'L4': true,
    'L7': true,
    'L9': true,
    'L10': true,
    'L11': true,
    'L12': true
  });
  const [definitionsFile, setDefinitionsFile] = useState(null);
  const [definitionsFileName, setDefinitionsFileName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [definitionsFileUrl, setDefinitionsFileUrl] = useState('');
  const [landscapeDate, setLandscapeDate] = useState('');
  const [landscapeDateModal, setLandscapeDateModal] = useState(null);
  const [modalLandscapeDate, setModalLandscapeDate] = useState('');
  const [colorPalette, setColorPalette] = useState('defaultBlue');
  const [colorPaletteModal, setColorPaletteModal] = useState(null);
  const [modalColorPalette, setModalColorPalette] = useState('');
  const [showDemoRequests, setShowDemoRequests] = useState(false);
  const [demoRequests, setDemoRequests] = useState([]);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistorySearch, setLoginHistorySearch] = useState('');

  function getDefaultEnabledViews() {
    const defaults = {};
    Object.keys(VIEW_TABS_CONFIG).forEach(key => {
      defaults[key] = VIEW_TABS_CONFIG[key].defaultEnabled;
    });
    return defaults;
  }

  useEffect(() => {
    loadExistingStudies();
    loadDemoRequests();
  }, []);

  const loadDemoRequests = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'demoRequests'));
      const requests = [];
      querySnapshot.forEach((doc) => {
        requests.push({ id: doc.id, ...doc.data() });
      });
      setDemoRequests(requests.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate)));
    } catch (error) {
      console.error('Error loading demo requests:', error);
    }
  };

  const updateDemoRequestStatus = async (requestId, newStatus) => {
    try {
      await setDoc(doc(db, 'demoRequests', requestId), { status: newStatus }, { merge: true });
      loadDemoRequests();
    } catch (error) {
      console.error('Error updating demo request:', error);
      alert('Failed to update status');
    }
  };

  const deleteDemoRequest = async (requestId) => {
    if (!window.confirm('Delete this demo request?')) return;
    try {
      await deleteDoc(doc(db, 'demoRequests', requestId));
      loadDemoRequests();
    } catch (error) {
      console.error('Error deleting demo request:', error);
      alert('Failed to delete request');
    }
  };

  const copyEmailToClipboard = (email) => {
    navigator.clipboard.writeText(email);
    alert('Email copied to clipboard!');
  };

  const loadLoginHistory = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'loginHistory'));
      const history = [];
      querySnapshot.forEach((doc) => {
        history.push({ id: doc.id, ...doc.data() });
      });
      setLoginHistory(history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    } catch (error) {
      console.error('Error loading login history:', error);
    }
  };

  const downloadLoginHistoryCSV = () => {
    if (loginHistory.length === 0) {
      alert('No login history to download');
      return;
    }

    const headers = ['Email', 'IP Address', 'Timestamp', 'Device', 'User Agent'];
    const rows = loginHistory.map(log => [
      log.email,
      log.ipAddress,
      new Date(log.timestamp).toLocaleString(),
      log.userAgent?.includes('Mobile') ? 'Mobile' : 'Desktop',
      log.userAgent || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `login_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadExistingStudies = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'studies'));
      const studies = [];
      querySnapshot.forEach((doc) => {
        studies.push({ id: doc.id, ...doc.data() });
      });
      // Show ALL studies (both active and archived) so admin can unarchive
      setExistingStudies(studies);
    } catch (err) {
      console.error('Error loading studies:', err);
    }
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = parseMultilineCSV(text);
      const headers = parseCSVLine(rows[0]).map(h => h.replace(/^["']|["']$/g, '').trim());
      setColumns(headers);
    };
    reader.readAsText(uploadedFile);
  };

  const handleSave = async () => {
    if (!studyName || !file) {
      alert('Please provide study name and file');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const rows = parseMultilineCSV(text);
        const headers = parseCSVLine(rows[0]).map(h => h.replace(/^["']|["']$/g, '').trim());
        
        const allowedUsersArray = allowedUsers
          .split(',')
          .map(email => email.trim())
          .filter(email => email);

        if (storageMethod === 'storage') {
          // Storage method: Upload CSV to Firebase Storage
          try {
            const storageRef = ref(storage, `studies/${studyName}/${file.name}`);
            await uploadBytes(storageRef, file);
            const fileURL = await getDownloadURL(storageRef);

            // Upload definitions file if provided
            let definitionsUrl = null;
            let defFileName = null;
            if (definitionsFile) {
              const defStorageRef = ref(storage, `definitions/${studyName}/${definitionsFile.name}`);
              await uploadBytes(defStorageRef, definitionsFile);
              definitionsUrl = await getDownloadURL(defStorageRef);
              defFileName = definitionsFile.name;
            }

            await setDoc(doc(db, 'studies', studyName), {
              name: studyName,
              storageType: 'storage',
              fileURL: fileURL,
              fileName: file.name,
              headers: headers,
              mapping,
              allowedUsers: allowedUsersArray,
              enabledViews: enabledViews,
              searchableFields: searchableFields,
              definitionsFileUrl: definitionsUrl,
              definitionsFileName: defFileName,
              landscapeDate: landscapeDate || null,
              colorPalette: colorPalette,
              createdAt: new Date(),
              createdBy: user.email
            });

            alert('Study saved successfully to Storage!');
          } catch (storageErr) {
            console.error('Storage upload error:', storageErr);
            alert('Storage upload failed. Error: ' + storageErr.message);
            setUploading(false);
            return;
          }
        } else {
          // Firestore method: Store CSV data in Firestore
          const csvData = rows.slice(1)
            .filter(row => row.trim())
            .map(row => {
              const values = parseCSVLine(row).map(v => v.replace(/^["']|["']$/g, '').trim());
              const rowData = {};
              headers.forEach((header, index) => {
                rowData[header] = values[index] || '';
              });
              return rowData;
            });

          // Upload definitions file if provided
          let definitionsUrl = null;
          let defFileName = null;
          if (definitionsFile) {
            const defStorageRef = ref(storage, `definitions/${studyName}/${definitionsFile.name}`);
            await uploadBytes(defStorageRef, definitionsFile);
            definitionsUrl = await getDownloadURL(defStorageRef);
            defFileName = definitionsFile.name;
          }

          await setDoc(doc(db, 'studies', studyName), {
            name: studyName,
            storageType: 'firestore',
            csvData: csvData,
            headers: headers,
            mapping,
            allowedUsers: allowedUsersArray,
            enabledViews: enabledViews,
            searchableFields: searchableFields,
            definitionsFileUrl: definitionsUrl,
            definitionsFileName: defFileName,
            landscapeDate: landscapeDate || null,
            colorPalette: colorPalette,
            createdAt: new Date(),
            createdBy: user.email
          });

          alert('Study saved successfully to Firestore!');
        }

        setStudyName('');
        setFile(null);
        setColumns([]);
        setMapping({});
        setAllowedUsers('');
        setEnabledViews(getDefaultEnabledViews());
        setDefinitionsFile(null);
        setDefinitionsFileName('');
        setDefinitionsFileUrl('');
        setLandscapeDate('');
        setColorPalette('defaultBlue');
        setUploading(false);
        loadExistingStudies();
      };
      
      reader.onerror = () => {
        alert('Error reading file');
        setUploading(false);
      };
      
      reader.readAsText(file);
    } catch (err) {
      alert('Error saving study: ' + err.message);
      setUploading(false);
    }
  };

  const handleArchive = async (studyName) => {
    try {
      const study = existingStudies.find(s => s.name === studyName);
      const newArchivedStatus = !study?.archived;
      await setDoc(doc(db, 'studies', studyName), { archived: newArchivedStatus }, { merge: true });
      alert(`Study ${newArchivedStatus ? 'archived' : 'unarchived'} successfully!`);
      loadExistingStudies();
    } catch (err) {
      alert('Error toggling archive: ' + err.message);
    }
  };

  const handleDelete = async (studyName) => {
    if (!window.confirm(`Delete study "${studyName}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'studies', studyName));
      loadExistingStudies();
    } catch (err) {
      alert('Error deleting study: ' + err.message);
    }
  };

  const handleDefinitionsFileUpload = async (e, studyName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = [
      'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
      'application/msword',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, DOCX, CSV, and Excel files are allowed');
      return;
    }
    
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `definitions/${studyName}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update Firestore
      await setDoc(doc(db, 'studies', studyName), {
        definitionsFileUrl: downloadURL,
        definitionsFileName: file.name
      }, { merge: true });
      
      alert('File uploaded successfully!');
      loadExistingStudies();
    } catch (err) {
      alert('Error uploading file: ' + err.message);
    }
  };

  const handleDefinitionsFileDelete = async (studyName) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    
    try {
      await setDoc(doc(db, 'studies', studyName), {
        definitionsFileUrl: null,
        definitionsFileName: null
      }, { merge: true });
      
      alert('File deleted successfully!');
      loadExistingStudies();
    } catch (err) {
      alert('Error deleting file: ' + err.message);
    }
  };

  const openPermissionsModal = (study) => {
    setPermissionsModal(study);
    setModalAllowedUsers((study.allowedUsers || []).join(', '));
  };

  const openMappingModal = (study) => {
    setMappingModal(study);
    setModalMapping(study.mapping || {});
  };

  const savePermissions = async () => {
    try {
      const allowedUsersArray = modalAllowedUsers
        .split(',')
        .map(email => email.trim())
        .filter(email => email);
      
      await setDoc(doc(db, 'studies', permissionsModal.name), 
        { allowedUsers: allowedUsersArray }, 
        { merge: true }
      );
      
      setPermissionsModal(null);
      loadExistingStudies();
      alert('Permissions updated successfully');
    } catch (err) {
      alert('Error updating permissions: ' + err.message);
    }
  };

  const saveMapping = async () => {
    try {
      await setDoc(doc(db, 'studies', mappingModal.name), 
        { mapping: modalMapping }, 
        { merge: true }
      );
      
      setMappingModal(null);
      loadExistingStudies();
      alert('Mapping updated successfully');
    } catch (err) {
      alert('Error updating mapping: ' + err.message);
    }
  };

  const openViewsModal = (study) => {
    setViewsModal(study);
    setModalEnabledViews(study.enabledViews || {});
  };

  const saveViews = async () => {
    try {
      await setDoc(doc(db, 'studies', viewsModal.name), 
        { enabledViews: modalEnabledViews }, 
        { merge: true }
      );
      
      setViewsModal(null);
      loadExistingStudies();
      alert('View tabs updated successfully');
    } catch (err) {
      alert('Error updating views: ' + err.message);
    }
  };

  const openSearchFieldsModal = (study) => {
    setSearchFieldsModal(study);
    setModalSearchableFields(study.searchableFields || {
      'Title': true,
      'Abstract': true,
      'L4': true,
      'L7': true,
      'L8': true,
      'L9': true,
      'L10': true,
      'L11': true,
      'L12': true
    });
  };

  const saveSearchFields = async () => {
    try {
      await setDoc(doc(db, 'studies', searchFieldsModal.name), 
        { searchableFields: modalSearchableFields }, 
        { merge: true }
      );
      
      setSearchFieldsModal(null);
      loadExistingStudies();
      alert('Searchable fields updated successfully');
    } catch (err) {
      alert('Error updating searchable fields: ' + err.message);
    }
  };

  const openLandscapeDateModal = (study) => {
    setLandscapeDateModal(study);
    setModalLandscapeDate(study.landscapeDate || '');
  };

  const saveLandscapeDate = async () => {
    try {
      await setDoc(doc(db, 'studies', landscapeDateModal.name), 
        { landscapeDate: modalLandscapeDate || null }, 
        { merge: true }
      );
      
      setLandscapeDateModal(null);
      loadExistingStudies();
      alert('Landscape date updated successfully');
    } catch (err) {
      alert('Error updating landscape date: ' + err.message);
    }
  };

  const openColorPaletteModal = (study) => {
    setColorPaletteModal(study);
    setModalColorPalette(study.colorPalette || 'defaultBlue');
  };

  const saveColorPalette = async () => {
    try {
      await setDoc(doc(db, 'studies', colorPaletteModal.name), 
        { colorPalette: modalColorPalette }, 
        { merge: true }
      );
      
      setColorPaletteModal(null);
      loadExistingStudies();
      alert('Color palette updated successfully');
    } catch (err) {
      alert('Error updating color palette: ' + err.message);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="admin-title">Admin Backend</h1>
        <div className="form-radio-group">
          <button
            onClick={() => setShowDemoRequests(!showDemoRequests)}
            className="btn-demo-requests"
          >
            Demo Requests {demoRequests.filter(r => r.status === 'pending').length > 0 && `(${demoRequests.filter(r => r.status === 'pending').length})`}
          </button>
          <button
            onClick={() => { setShowLoginHistory(!showLoginHistory); if (!showLoginHistory) loadLoginHistory(); }}
            className="btn-login-history"
          >
            Login History
          </button>
          <span className="admin-email">{user.email}</span>
          <button
            onClick={onLogout}
            className="btn-logout"
          >
            Logout
          </button>
        </div>
      </div>
      
      {/* Existing Studies Section */}
      {existingStudies.length > 0 && (
        <div className="studies-section">
          <div className="studies-header">
            <h2 className="studies-title">Existing Studies</h2>
            <button
              onClick={loadExistingStudies}
              className="btn-refresh"
            >
              Refresh
            </button>
          </div>
          <div className="studies-list">
            {existingStudies.map(study => (
              <div key={study.id} className={`flex items-center justify-between p-3 border rounded ${study.archived ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                <div className="study-info">
                  <div className="study-name-row">
                    <span className="study-name">{study.name}</span>
                    {study.archived && (
                      <span className="badge-archived">ARCHIVED</span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${
                      study.storageType === 'storage' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {study.storageType === 'storage' ? 'STORAGE' : 'FIRESTORE'}
                    </span>
                  </div>
                  <div className="admin-email">
                    Created: {study.createdAt?.toDate?.().toLocaleDateString() || 'N/A'} | 
                    By: {study.createdBy || 'Unknown'}
                  </div>
                  <div className="admin-email">
                    Access: {study.allowedUsers && study.allowedUsers.length > 0 
                      ? `${study.allowedUsers.length} user(s)` 
                      : 'All users'}
                  </div>
                  {study.definitionsFileName && (
                    <div className="text-sm text-green-600 flex items-center gap-1">
                      <i data-lucide="file-text" className="w-4 h-4"></i> File: {study.definitionsFileName}
                    </div>
                  )}
                  {study.landscapeDate && (
                    <div className="text-sm text-blue-600">
                      Landscaped: {new Date(study.landscapeDate + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                  )}
                  {study.colorPalette && (
                    <div className="text-sm text-purple-600">
                      Colors: {COLOR_PALETTES[study.colorPalette]?.name || 'Default Blue'}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {study.definitionsFileName ? (
                    <>
                      <a
                        href={study.definitionsFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-action btn-view-def"
                      >
                        View file
                      </a>
                      <button
                        onClick={() => handleDefinitionsFileDelete(study.name)}
                        className="btn-action btn-del-def"
                      >
                        Del file
                      </button>
                    </>
                  ) : (
                    <label className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 cursor-pointer">
                      Add file
                      <input
                        type="file"
                        accept=".pdf,.docx,.doc,.csv,.xlsx,.xls"
                        onChange={(e) => handleDefinitionsFileUpload(e, study.name)}
                        className="hidden"
                      />
                    </label>
                  )}
                  <button
                    onClick={() => openPermissionsModal(study)}
                    className="btn-action btn-permissions"
                  >
                    Permissions
                  </button>
                  <button
                    onClick={() => openMappingModal(study)}
                    className="btn-action btn-mapping"
                  >
                    Mapping
                  </button>
                  <button
                    onClick={() => openViewsModal(study)}
                    className="btn-action btn-views"
                  >
                    Views
                  </button>
                  <button
                    onClick={() => openSearchFieldsModal(study)}
                    className="btn-action btn-search-fields"
                  >
                    Search Fields
                  </button>
                  <button
                    onClick={() => openLandscapeDateModal(study)}
                    className="bg-cyan-500 text-white px-3 py-1 rounded text-sm hover:bg-cyan-600"
                  >
                    Landscape Date
                  </button>
                  <button
                    onClick={() => openColorPaletteModal(study)}
                    className="bg-purple-500 text-white px-3 py-1 rounded text-sm hover:bg-purple-600"
                  >
                    Color Palette
                  </button>
                  <button
                    onClick={() => handleArchive(study.name)}
                    className={`px-3 py-1 rounded text-sm ${
                      study.archived 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-yellow-500 text-white hover:bg-yellow-600'
                    }`}
                  >
                    {study.archived ? 'Unarchive' : 'Archive'}
                  </button>
                  <button
                    onClick={() => handleDelete(study.name)}
                    className="btn-action btn-delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demo Requests Section */}
      {showDemoRequests && (
        <div className="studies-section">
          <div className="studies-header">
            <h2 className="studies-title">Demo Access Requests</h2>
            <button
              onClick={loadDemoRequests}
              className="btn-refresh"
            >
              Refresh
            </button>
          </div>
          {demoRequests.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No demo requests yet</p>
          ) : (
            <div className="studies-list">
              {demoRequests.map(req => (
                <div key={req.id} className={`p-4 border rounded ${req.status === 'pending' ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="study-info">
                      <div className="font-semibold text-lg">{req.name}</div>
                      <div className="admin-email">{req.organization}</div>
                      <div className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => copyEmailToClipboard(req.email)}>
                        {req.email} (click to copy)
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Requested: {new Date(req.requestDate).toLocaleString()}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded text-sm font-medium ${
                      req.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                      req.status === 'approved' ? 'bg-green-200 text-green-800' :
                      'bg-red-200 text-red-800'
                    }`}>
                      {req.status.toUpperCase()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {req.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateDemoRequestStatus(req.id, 'approved')}
                          className="btn-action btn-view-def"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateDemoRequestStatus(req.id, 'rejected')}
                          className="btn-action btn-delete"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => deleteDemoRequest(req.id)}
                      className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Login History Section */}
      {showLoginHistory && (
        <div className="studies-section">
          <div className="studies-header">
            <h2 className="studies-title">Login History</h2>
            <div className="flex gap-2">
              <button
                onClick={downloadLoginHistoryCSV}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm"
              >
                Download CSV
              </button>
              <button
                onClick={loadLoginHistory}
                className="btn-refresh"
              >
                Refresh
              </button>
            </div>
          </div>
          
          {/* Search Box */}
          <div className="form-group">
            <input
              type="text"
              placeholder="Search by email..."
              value={loginHistorySearch}
              onChange={(e) => setLoginHistorySearch(e.target.value)}
              className="w-full p-2 border rounded text-sm"
            />
          </div>

          {loginHistory.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No login history yet</p>
          ) : (
            <div className="patent-results-table-wrapper">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">IP Address</th>
                    <th className="p-2 text-left">Timestamp</th>
                    <th className="p-2 text-left">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {loginHistory
                    .filter(log => 
                      loginHistorySearch === '' || 
                      log.email.toLowerCase().includes(loginHistorySearch.toLowerCase())
                    )
                    .map(log => (
                      <tr key={log.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{log.email}</td>
                        <td className="p-2 font-mono text-xs">{log.ipAddress}</td>
                        <td className="p-2 text-xs">{new Date(log.timestamp).toLocaleString()}</td>
                        <td className="p-2 text-xs truncate max-w-xs" title={log.userAgent}>
                          {log.userAgent?.includes('Mobile') ? '📱 Mobile' : '💻 Desktop'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create New Study Form */}
      <div className="create-study-form">
        <h2 className="form-title">Create New Study</h2>
        
        <div className="form-group">
          <label className="form-label">Study Name:</label>
          <input
            type="text"
            placeholder="Enter study name"
            value={studyName}
            onChange={(e) => setStudyName(e.target.value)}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Storage Method:</label>
          <div className="form-radio-group">
            <label className="study-name-row">
              <input
                type="radio"
                value="firestore"
                checked={storageMethod === 'firestore'}
                onChange={(e) => setStorageMethod(e.target.value)}
              />
              <span className="searchable-field-label">Firestore (up to ~500 rows)</span>
            </label>
            <label className="study-name-row">
              <input
                type="radio"
                value="storage"
                checked={storageMethod === 'storage'}
                onChange={(e) => setStorageMethod(e.target.value)}
              />
              <span className="searchable-field-label">Storage (for 1000+ rows)</span>
            </label>
          </div>
          <p className="form-help-text">
            Stores data directly in Firestore. Limited to ~500 rows due to 1MB document size limit.
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Upload Patent File (CSV):</label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="form-input"
          />
        </div>

        {/* Column Mapping Section */}
        {columns.length > 0 && (
          <div className="form-group">
            <h3 className="column-mapping-title">Column Mapping</h3>
            <p className="column-mapping-help">Map your CSV columns to the required fields</p>
            
            <div className="column-mapping-list">
              {Object.entries(MAPPING_FIELDS).map(([categoryKey, category]) => (
                <div key={categoryKey}>
                  <h4 className="mapping-category-title">{category.label}</h4>
                  <div className="mapping-fields">
                    {Object.entries(category.fields).map(([fieldKey, field]) => (
                      <div key={fieldKey} className="study-name-row">
                        <label className="text-xs font-medium w-48 flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={mapping[fieldKey] || ''}
                          onChange={(e) => setMapping({ ...mapping, [fieldKey]: e.target.value })}
                          className="mapping-field-select"
                        >
                          <option value="">-- Select Column --</option>
                          {columns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Enable View Tabs:</label>
          <p className="column-mapping-help">
            Select which analysis views should be available for this study. You can enable different combinations based on your data.
          </p>
          <div className="view-tabs-list">
            {Object.entries(VIEW_TABS_CONFIG).map(([key, config]) => (
              <label key={key} className="view-tab-item">
                <input
                  type="checkbox"
                  checked={enabledViews[key] || false}
                  onChange={(e) => setEnabledViews({ ...enabledViews, [key]: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <div className="view-tab-label">{config.label}</div>
                  <div className="view-tab-description">{config.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Searchable Fields (Text Search):</label>
          <p className="column-mapping-help">
            Select which fields should be included in the universal text search.
          </p>
          <div className="searchable-fields-grid">
            {Object.keys(searchableFields).map(field => (
              <label key={field} className="study-name-row">
                <input
                  type="checkbox"
                  checked={searchableFields[field] || false}
                  onChange={(e) => setSearchableFields({ ...searchableFields, [field]: e.target.checked })}
                />
                <span className="searchable-field-label">{field}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Allowed Users (comma-separated emails):</label>
          <textarea
            placeholder="user1@test.com, user2@test.com"
            value={allowedUsers}
            onChange={(e) => setAllowedUsers(e.target.value)}
            className="form-textarea"
          />
          <p className="form-help-text">
            Only these users can view this study. Leave blank for all users.
          </p>
        </div>

        <div className="mb-4 border-t pt-4">
          <label className="form-label">Definitions Document (PDF/DOCX/CSV/Excel):</label>
          <p className="column-mapping-help">
            Upload a document with L4 and L7 definitions, or landscaped data (CSV/Excel) for this study. Users will be able to download it from the main view.
          </p>
          <input
            type="file"
            accept=".pdf,.docx,.doc,.csv,.xlsx,.xls"
            onChange={(e) => {
              const selectedFile = e.target.files[0];
              if (selectedFile) {
                setDefinitionsFile(selectedFile);
                setDefinitionsFileName(selectedFile.name);
              }
            }}
            className="form-input"
          />
          {definitionsFileName && (
            <p className="text-sm text-green-600 mt-2">Selected: {definitionsFileName}</p>
          )}
        </div>

        <div className="mb-4 border-t pt-4">
          <label className="form-label">Landscape Date (Optional):</label>
          <p className="column-mapping-help">
            When were these patents analyzed/landscaped? This date will be shown to customers.
          </p>
          <input
            type="month"
            value={landscapeDate}
            onChange={(e) => setLandscapeDate(e.target.value)}
            className="p-2 border rounded"
            placeholder="YYYY-MM"
          />
          {landscapeDate && (
            <p className="text-sm text-green-600 mt-2">Will display as: {new Date(landscapeDate + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>
          )}
        </div>

        <div className="mb-4 border-t pt-4">
          <label className="form-label">Color Palette:</label>
          <p className="column-mapping-help">
            Select a color scheme for this study's treemap visualizations (Basic, Innovation, Advanced tabs).
          </p>
          <select
            value={colorPalette}
            onChange={(e) => setColorPalette(e.target.value)}
            className="p-2 border rounded w-full"
          >
            {Object.entries(COLOR_PALETTES).map(([key, palette]) => (
              <option key={key} value={key}>{palette.name}</option>
            ))}
          </select>
          <div className="mt-2 flex gap-1">
            <p className="text-sm text-gray-600">Preview:</p>
            {COLOR_PALETTES[colorPalette].monochrome.map((color, idx) => (
              <div
                key={idx}
                className="w-8 h-8 rounded border"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={uploading}
          className="btn-save-study"
        >
          {uploading ? 'Saving...' : 'Save Study'}
        </button>
      </div>

      {/* Permissions Modal */}
      {permissionsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">Edit Permissions: {permissionsModal.name}</h2>
            <label className="form-label">Allowed Users (comma-separated emails):</label>
            <textarea
              value={modalAllowedUsers}
              onChange={(e) => setModalAllowedUsers(e.target.value)}
              className="w-full p-2 border rounded h-32 mb-4"
              placeholder="user1@test.com, user2@test.com"
            />
            <p className="text-xs text-gray-600 mb-4">Leave blank for all users.</p>
            <div className="modal-footer">
              <button
                onClick={() => setPermissionsModal(null)}
                className="btn-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={savePermissions}
                className="btn-modal-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mapping Modal */}
      {mappingModal && (
        <div className="modal-overlay">
          <div className="modal-content large scrollable">
            <h2 className="text-xl font-bold mb-2">Edit Column Mapping: {mappingModal.name}</h2>
            <p className="text-sm text-gray-600 mb-4">Map your CSV columns to the required fields below</p>
            
            {/* Available CSV Columns Info */}
            {mappingModal.headers && (
              <div className="mb-4 p-3 bg-blue-50 rounded">
                <p className="text-sm font-semibold mb-1">Available CSV Columns ({mappingModal.headers.length}):</p>
                <p className="text-xs text-gray-700">{mappingModal.headers.join(', ')}</p>
              </div>
            )}

            <div className="space-y-6">
              {Object.entries(MAPPING_FIELDS).map(([categoryKey, category]) => (
                <div key={categoryKey} className="border rounded p-4">
                  <h3 className="font-semibold text-md mb-3">{category.label}</h3>
                  <div className="view-tabs-list">
                    {Object.entries(category.fields).map(([fieldKey, field]) => (
                      <div key={fieldKey} className="flex items-center gap-3">
                        <label className="text-sm font-medium w-64 flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={modalMapping[fieldKey] || ''}
                          onChange={(e) => setModalMapping({ ...modalMapping, [fieldKey]: e.target.value })}
                          className="flex-1 p-2 border rounded text-sm"
                        >
                          <option value="">-- Select Column --</option>
                          {mappingModal.headers && mappingModal.headers.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setMappingModal(null)}
                className="btn-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveMapping}
                className="btn-modal-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Views Modal */}
      {viewsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">Edit View Tabs: {viewsModal.name}</h2>
            <p className="text-sm text-gray-600 mb-4">Select which analysis views should be available for this study.</p>
            
            <div className="space-y-2 mb-6">
              {Object.entries(VIEW_TABS_CONFIG).map(([key, config]) => (
                <label key={key} className="view-tab-item">
                  <input
                    type="checkbox"
                    checked={modalEnabledViews[key] || false}
                    onChange={(e) => setModalEnabledViews({ ...modalEnabledViews, [key]: e.target.checked })}
                    className="mt-1"
                  />
                  <div>
                    <div className="view-tab-label">{config.label}</div>
                    <div className="view-tab-description">{config.description}</div>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => setViewsModal(null)}
                className="btn-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveViews}
                className="btn-modal-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Fields Modal */}
      {searchFieldsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">Edit Searchable Fields: {searchFieldsModal.name}</h2>
            <p className="text-sm text-gray-600 mb-4">Select which fields should be included in the universal text search.</p>
            
            <div className="grid grid-cols-3 gap-2 mb-6">
              {Object.keys(modalSearchableFields).map(field => (
                <label key={field} className="study-name-row">
                  <input
                    type="checkbox"
                    checked={modalSearchableFields[field] || false}
                    onChange={(e) => setModalSearchableFields({ ...modalSearchableFields, [field]: e.target.checked })}
                  />
                  <span className="searchable-field-label">{field}</span>
                </label>
              ))}
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => setSearchFieldsModal(null)}
                className="btn-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveSearchFields}
                className="btn-modal-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Landscape Date Modal */}
      {landscapeDateModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Edit Landscape Date: {landscapeDateModal.name}</h2>
            <p className="text-sm text-gray-600 mb-4">Set when these patents were analyzed/landscaped. This date will be shown to customers in the footer.</p>
            
            <div className="mb-6">
              <label className="form-label">Landscape Date:</label>
              <input
                type="month"
                value={modalLandscapeDate}
                onChange={(e) => setModalLandscapeDate(e.target.value)}
                className="form-input"
                placeholder="YYYY-MM"
              />
              {modalLandscapeDate && (
                <p className="text-sm text-blue-600 mt-2">
                  Will display as: {new Date(modalLandscapeDate + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => setLandscapeDateModal(null)}
                className="btn-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveLandscapeDate}
                className="px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Color Palette Modal */}
      {colorPaletteModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Edit Color Palette: {colorPaletteModal.name}</h2>
            <p className="text-sm text-gray-600 mb-4">Select a color scheme for this study's visualizations.</p>
            
            <div className="mb-6">
              <label className="form-label">Color Palette:</label>
              <select
                value={modalColorPalette}
                onChange={(e) => setModalColorPalette(e.target.value)}
                className="form-input"
              >
                {Object.entries(COLOR_PALETTES).map(([key, palette]) => (
                  <option key={key} value={key}>{palette.name}</option>
                ))}
              </select>
              
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Preview - Monochrome (for Basic & Innovation):</p>
                <div className="flex gap-1">
                  {COLOR_PALETTES[modalColorPalette].monochrome.map((color, idx) => (
                    <div
                      key={idx}
                      className="w-12 h-12 rounded border"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Preview - Multicolor (for Advanced):</p>
                <div className="flex gap-1 flex-wrap">
                  {COLOR_PALETTES[modalColorPalette].multicolor.map((color, idx) => (
                    <div
                      key={idx}
                      className="w-10 h-10 rounded border"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => setColorPaletteModal(null)}
                className="btn-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveColorPalette}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// BOOLEAN SEARCH UTILITIES
// ============================================
const parseSearchQuery = (query) => {
  if (!query || !query.trim()) return null;
  
  const tokens = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  
  while (i < query.length) {
    const char = query[i];
    
    if (char === '"') {
      if (inQuotes && current) {
        tokens.push({ type: 'phrase', value: current });
        current = '';
      }
      inQuotes = !inQuotes;
    } else if (!inQuotes && char === '(') {
      // Add current term if exists
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
      tokens.push({ type: 'bracket', value: '(' });
    } else if (!inQuotes && char === ')') {
      // Add current term if exists
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
      tokens.push({ type: 'bracket', value: ')' });
    } else if (!inQuotes && (char === '|' || char === '+' || char === '-')) {
      // Add current term if exists
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
      // Add operator
      if (char === '|') tokens.push({ type: 'operator', value: 'OR' });
      else if (char === '+') tokens.push({ type: 'operator', value: 'AND' });
      else if (char === '-') tokens.push({ type: 'operator', value: 'NOT' });
    } else if (!inQuotes && char === ' ') {
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
    } else {
      current += char;
    }
    i++;
  }
  
  // If still in quotes, query is incomplete - return null to skip filtering
  if (inQuotes) return null;
  
  // Add final term
  if (current.trim()) {
    tokens.push({ type: 'term', value: current.trim() });
  }
  
  // Check for balanced brackets
  let bracketCount = 0;
  for (const token of tokens) {
    if (token.type === 'bracket') {
      if (token.value === '(') bracketCount++;
      else if (token.value === ')') bracketCount--;
      if (bracketCount < 0) return null; // Unbalanced
    }
  }
  if (bracketCount !== 0) return null; // Unbalanced
  
  // If no valid tokens, return null
  if (tokens.length === 0) return null;
  
  // If single term or phrase, return as-is
  if (tokens.length === 1) {
    return tokens[0];
  }
  
  // Default: space-separated terms = implicit AND
  return { type: 'parsed', tokens };
};

const evaluateSearch = (text, parsedQuery) => {
  if (!parsedQuery || !text) return false;
  
  const lowerText = text.toLowerCase();
  
  // Helper: convert wildcard to regex pattern
  const wildcardToRegex = (pattern) => {
    const escapedPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    const regexPattern = escapedPattern.replace(/\*/g, '.*');
    return new RegExp(regexPattern, 'i');
  };
  
  // Helper: check if text matches pattern (with wildcard support)
  const matchesPattern = (text, pattern) => {
    if (pattern.includes('*')) {
      // Wildcard: use pattern matching
      return wildcardToRegex(pattern).test(text);
    } else {
      // No wildcard: use word boundary matching
      const escapedPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
      const wordBoundaryRegex = new RegExp('\\b' + escapedPattern + '\\b', 'i');
      return wordBoundaryRegex.test(text);
    }
  };
  
  // Single term or phrase
  if (parsedQuery.type === 'term' || parsedQuery.type === 'phrase') {
    return matchesPattern(lowerText, parsedQuery.value);
  }
  
  // Helper: process bracketed expressions recursively
  const processBrackets = (tokens) => {
    // Find innermost brackets and replace with evaluated result
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type === 'bracket' && tokens[i].value === '(') {
        // Find matching closing bracket
        let depth = 1;
        let j = i + 1;
        while (j < tokens.length && depth > 0) {
          if (tokens[j].type === 'bracket') {
            if (tokens[j].value === '(') depth++;
            else if (tokens[j].value === ')') depth--;
          }
          j++;
        }
        
        // Extract bracketed tokens (excluding brackets themselves)
        const bracketedTokens = tokens.slice(i + 1, j - 1);
        
        // Recursively evaluate bracketed expression
        const bracketResult = evaluateTokens(bracketedTokens);
        
        // Replace bracketed section with result
        const resultToken = { type: 'result', value: bracketResult };
        tokens.splice(i, j - i, resultToken);
        
        // Process again from start (in case of nested brackets)
        return processBrackets(tokens);
      }
    }
    return tokens;
  };
  
  // Helper: evaluate tokens without brackets
  const evaluateTokens = (tokens) => {
    if (tokens.length === 0) return false;
    
    // If single result token, return its value
    if (tokens.length === 1 && tokens[0].type === 'result') {
      return tokens[0].value;
    }
    
    // If single term/phrase, evaluate it
    if (tokens.length === 1 && (tokens[0].type === 'term' || tokens[0].type === 'phrase')) {
      return matchesPattern(lowerText, tokens[0].value);
    }
    
    let results = [];
    let currentOp = 'AND'; // Default implicit AND
    let nextNot = false;
    
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      
      if (token.type === 'operator') {
        if (token.value === 'NOT') {
          nextNot = true;
        } else {
          currentOp = token.value;
        }
      } else if (token.type === 'result') {
        // Pre-evaluated result from bracket
        let found = token.value;
        
        if (nextNot) {
          found = !found;
          nextNot = false;
        }
        
        // Combine with previous results
        if (results.length === 0) {
          results.push(found);
        } else {
          if (currentOp === 'AND') {
            results.push(results.pop() && found);
          } else if (currentOp === 'OR') {
            results.push(results.pop() || found);
          }
          currentOp = 'AND'; // Reset to implicit AND
        }
      } else {
        // Evaluate term/phrase with wildcard support
        let found = matchesPattern(lowerText, token.value);
        
        if (nextNot) {
          found = !found;
          nextNot = false;
        }
        
        // Combine with previous results
        if (results.length === 0) {
          results.push(found);
        } else {
          if (currentOp === 'AND') {
            results.push(results.pop() && found);
          } else if (currentOp === 'OR') {
            results.push(results.pop() || found);
          }
          currentOp = 'AND'; // Reset to implicit AND
        }
      }
    }
    
    return results.length > 0 ? results[0] : false;
  };
  
  // Process brackets first (innermost to outermost)
  const tokens = parsedQuery.tokens;
  const processedTokens = processBrackets([...tokens]);
  
  // Evaluate final expression
  return evaluateTokens(processedTokens);
};

// ============================================
// CENTRALIZED FILTER FUNCTIONS
// ============================================

// Check if patent matches applicant filter
const matchesApplicant = (patent, selectedApplicant) => {
  if (!selectedApplicant) return true;
  
  if (patent.applicant.includes(';')) {
    return patent.applicant.split(';').map(a => a.trim()).includes(selectedApplicant);
  }
  return patent.applicant === selectedApplicant;
};

// Convert title to sentence case
const toSentenceCase = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Normalize patent number (remove spaces, hyphens, slashes)
const normalizePatentNo = (str) => {
  if (!str) return '';
  return str.toUpperCase().replace(/[\s\-\/]/g, '');
};

// Check if patent matches text search filter
const matchesTextSearch = (patent, textSearch, searchableFields) => {
  if (!textSearch) return true;
  
  const parsedQuery = parseSearchQuery(textSearch);
  if (!parsedQuery) return true;
  
  const fields = Object.entries(searchableFields || {})
    .filter(([_, enabled]) => enabled)
    .map(([field, _]) => field);
  
  return fields.some(field => {
    const value = patent[field.toLowerCase()] || '';
    return evaluateSearch(value, parsedQuery);
  });
};

// Check if patent matches L2 genus filter
const matchesGenus = (patent, selectedL2) => {
  if (!selectedL2) return true;
  return patent.l2 === selectedL2;
};

// Master filter function - applies all filters
const applyFilters = (patents, filters) => {
  const { selectedApplicant, textSearch, searchableFields, selectedL2 } = filters;
  
  return patents.filter(patent => {
    return matchesApplicant(patent, selectedApplicant) &&
           matchesTextSearch(patent, textSearch, searchableFields) &&
           matchesGenus(patent, selectedL2);
  });
};

// ============================================
// CUSTOMER VIEW
// ============================================

export { AdminBackend, COLOR_PALETTES };
